"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Toast } from "./Toast";
import { ToastContext } from "./toastContext";
import type {
  ShowToastPayload,
  ToastApi,
  ToastItem,
  ToastOptions,
  ToastType,
} from "./types";

const DEFAULT_DURATION_MS = 3000;
const MAX_TOASTS = 5;

type ToastState = { items: ToastItem[] };

type Action =
  | { type: "ADD"; item: ToastItem }
  | { type: "REMOVE"; id: string }
  | { type: "CLEAR" };

function reducer(state: ToastState, action: Action): ToastState {
  switch (action.type) {
    case "ADD":
      return {
        items: [...state.items, action.item].slice(-MAX_TOASTS),
      };
    case "REMOVE":
      return { items: state.items.filter((i) => i.id !== action.id) };
    case "CLEAR":
      return { items: [] };
    default:
      return state;
  }
}

function createId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { items: [] });
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current.clear();
  }, []);

  const dismiss = useCallback((id: string) => {
    const t = timeoutsRef.current.get(id);
    if (t) clearTimeout(t);
    timeoutsRef.current.delete(id);
    dispatch({ type: "REMOVE", id });
  }, []);

  const scheduleDismiss = useCallback(
    (id: string, duration: number) => {
      const existing = timeoutsRef.current.get(id);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => dismiss(id), duration);
      timeoutsRef.current.set(id, t);
    },
    [dismiss],
  );

  const pushToast = useCallback(
    (
      type: ToastType,
      message: ReactNode,
      durationMs: number | undefined,
    ): string => {
      const id = createId();
      const duration = durationMs ?? DEFAULT_DURATION_MS;
      const item: ToastItem = { id, type, message, duration };
      dispatch({ type: "ADD", item });
      scheduleDismiss(id, duration);
      return id;
    },
    [scheduleDismiss],
  );

  const show = useCallback(
    (payload: ShowToastPayload): string =>
      pushToast(payload.type, payload.message, payload.duration),
    [pushToast],
  );

  const success = useCallback(
    (message: ReactNode, options?: ToastOptions) =>
      pushToast("success", message, options?.duration),
    [pushToast],
  );

  const error = useCallback(
    (message: ReactNode, options?: ToastOptions) =>
      pushToast("error", message, options?.duration),
    [pushToast],
  );

  const info = useCallback(
    (message: ReactNode, options?: ToastOptions) =>
      pushToast("info", message, options?.duration),
    [pushToast],
  );

  const clear = useCallback(() => {
    clearTimers();
    dispatch({ type: "CLEAR" });
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success,
      error,
      info,
      dismiss,
      clear,
    }),
    [show, success, error, info, dismiss, clear],
  );

  const portal =
    mounted &&
    typeof document !== "undefined" &&
    createPortal(<Toast items={state.items} />, document.body);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {portal}
    </ToastContext.Provider>
  );
}
